import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Image,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { GoogleMap } from '@react-google-maps/api';
import { useSublocations } from '../hooks/useSublocations';
import { useSiteData } from '../hooks/useSiteData';
import { useMedia } from '../hooks/useMedia';
import SiteMarker from '../components/SiteMarker';
import SubLocationMarker from '../components/SubLocationMarker';

const mapContainerStyle = {
  width: '100%',
  height: '80vh'
};

export default function SubLocationMapViewer() {
  const [selectedSublocation, setSelectedSublocation] = useState(null);
  const { id } = useParams();
  const { site, loading: siteLoading } = useSiteData(id);
  const { sublocations, loading: subLocationsLoading } = useSublocations(id);
  const { media, loading: mediaLoading, loadMedia } = useMedia(id);

  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  
  // Load media when component mounts or siteId changes
  React.useEffect(() => {
    const loadMediaData = async () => {
      if (!id) return;
      
      setIsLoadingMedia(true);
      try {
        await loadMedia();
      } catch (err) {
        console.error('Error loading media:', err);
      }
      setIsLoadingMedia(false);
    };

    loadMediaData();
  }, [id, loadMedia]);

  React.useEffect(() => {
    if (selectedSublocation && media && media.length > 0) {
      console.log('Selected sublocation:', selectedSublocation.id);
      const matchingMedia = media.filter(m => m.subLocationId === selectedSublocation.id);
      console.log('Found matching media:', matchingMedia);
    }
  }, [selectedSublocation, media]);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const loading = siteLoading || subLocationsLoading || mediaLoading || isLoadingMedia;

  if (loading) {
    return (
      <Container maxW="container.xl" py={10}>
        <VStack spacing={4} align="center">
          <Text>Loading map data...</Text>
          <Spinner />
        </VStack>
      </Container>
    );
  }

  const center = site?.location?.coordinates || { lat: 0, lng: 0 };

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box 
          bg={bgColor}
          borderRadius="lg"
          overflow="hidden"
          boxShadow="md"
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={14}
            options={{
              mapTypeId: 'satellite',
              mapTypeControl: true,
              scaleControl: true,
              streetViewControl: true,
              rotateControl: true,
              fullscreenControl: true
            }}
          >
            {/* Main Site Marker */}
            <SiteMarker
              position={center}
              site={site}
            />

            {/* Sublocation Markers */}
            {sublocations?.map(sublocation => (
              <SubLocationMarker
                key={sublocation.id}
                position={sublocation.coordinates}
                sublocation={sublocation}
                onClick={(sublocation) => {
                  setSelectedSublocation(sublocation);
                }}
              />
            ))}
          </GoogleMap>
        </Box>

        {/* Selected Sublocation Details and Media */}
        {selectedSublocation && (
          <Box 
            bg={bgColor}
            p={6}
            borderRadius="lg"
            boxShadow="md"
          >
            <VStack spacing={4} align="start" width="100%">
              <Text fontSize="xl" fontWeight="bold">
                {selectedSublocation.name}
              </Text>
              
              {/* Display sublocation description if available */}
              {selectedSublocation.description && (
                <Text color="gray.600">{selectedSublocation.description}</Text>
              )}

              {/* Debug info */}
              <Box p={2} bg="red.50" borderRadius="md" mb={4}>
                <Text color="red.600" fontSize="sm" fontFamily="monospace">
                  Debug State:
                </Text>
                <Text color="red.600" fontSize="sm" fontFamily="monospace" whiteSpace="pre-wrap">
                  Loading: {loading ? 'true' : 'false'}
                  {'\n'}Total Media: {media?.length || 0}
                  {'\n'}Selected ID: {selectedSublocation?.id}
                  {'\n'}Matches: {media?.filter(m => m.subLocationId === selectedSublocation?.id).length || 0}
                  {'\n\n'}Matching Media Items:
                  {'\n'}{media?.filter(m => m.subLocationId === selectedSublocation?.id)
                    .map(m => `- ${m.id}: ${m.url}`).join('\n')}
                </Text>
              </Box>
              
              {/* Display media associated with this sublocation */}
              {media
                .filter(m => m.subLocationId === selectedSublocation.id)
                .map((mediaItem, index) => (
                  <Box key={mediaItem.id} width="100%">
                    <Image
                      src={mediaItem.url}
                      alt={`${selectedSublocation.name} - Image ${index + 1}`}
                      maxH="400px"
                      objectFit="contain"
                      width="100%"
                      borderRadius="md"
                    />
                    {mediaItem.description && (
                      <Text mt={2} color="gray.600">
                        {mediaItem.description}
                      </Text>
                    )}
                  </Box>
                ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
